import { Loader } from '@storybook';
import { FC, useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { loginState } from 'global-stores';
import { envHost } from 'helpers';
import { useNetwork } from 'hooks';
import { InjectScript } from 'utils/script';
import { REACT_APP_SESSION_WEB_COMPONENT as WEB_COMPONENT } from 'envs';
import './business-session.scss';
import { SignUpSessionStatusState } from 'views/new-user-popup';
import { useCompliance } from 'routes/hooks';

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			'session-details': any;
		}
	}
}

interface IBusinessSession {
	handleError?: (e: any) => void;
}

/**
 * The sessions bundle is loaded ONCE per page, the way `loadEsignWebComponents`
 * loads the esign one. Its loader stub is a classic script with top-level
 * `const`s, so running it a second time throws "Identifier has already been
 * declared" on its first line; before this it was re-injected on every open.
 *
 * The stub appends the sessions app's whole main.css to <head>, UNSCOPED —
 * `body {background:#fff; color:#495057}`, `html {font-size}`,
 * `* {text-align:left}` and its own `:root {--color-*}` palette — plus
 * remixicon 2.5 over the panel's 4.2. Appended after the panel's CSS it wins
 * every same-specificity tie, and it stayed in <head> after the report closed:
 * the page behind the modal rendered white while still carrying the dark
 * theme's text colours, and the Logout button vanished into it (SIM-50). The
 * sheets the stub added are kept but DISABLED while no report is mounted;
 * `disabled` is the native switch for exactly this and costs no re-fetch when
 * the report reopens.
 */
let injection: Promise<void> | null = null;
let sessionSheets: HTMLLinkElement[] = [];
/** Mounted reports. Sheets are on while this is above zero. */
let mounted = 0;

const loadSessionsWebComponent = (): Promise<void> => {
	if (!injection) {
		// Found by diff, not by href: the stub's CDN host differs per environment
		// and the stub appends its sheets synchronously, before its own onload.
		const before = new Set(document.querySelectorAll('link[rel=stylesheet]'));
		injection = InjectScript([{ type: 'script', link: WEB_COMPONENT }]).then(
			() => {
				sessionSheets = Array.from(
					document.querySelectorAll<HTMLLinkElement>('link[rel=stylesheet]')
				).filter(link => !before.has(link));
				// InjectScript logs a failed fetch and resolves anyway. The stub
				// always appends its sheets, so none means it never ran — let the
				// next open try again instead of serving a blank report for good.
				if (!sessionSheets.length) injection = null;
			}
		);
	}
	return injection;
};

const syncSessionSheets = () =>
	sessionSheets.forEach(link => {
		link.disabled = mounted === 0;
	});

export const BusinessSession: FC<IBusinessSession> = ({ handleError }) => {
	const userDetails = useRecoilValue(loginState);
	const [isLoading, setIsLoading] = useState(true);
	const [sessionId, setSessionId] = useState('');
	const signUpSessionStatusState = useRecoilValue(SignUpSessionStatusState);

	const { get } = useNetwork();
	const { getComplianceData } = useCompliance();

	const handleSessionId = useCallback(async () => {
		try {
			if (userDetails?.business) {
				const res = await get(
					`/business-compliance?businessId=${userDetails.business}`
				);
				if (res?.data?.[0]?.sessionDetails?.[0]?.sessionId) {
					setSessionId(res.data[0].sessionDetails[0].sessionId);
				} else {
					handleError?.(res);
				}
			}
		} finally {
			setIsLoading(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const appendScripts = async () => {
		await getComplianceData();
		await loadSessionsWebComponent();
		// Reads the live count: a report closed mid-load leaves the sheets off.
		syncSessionSheets();
		await handleSessionId();
		setIsLoading(false);
	};

	useEffect(() => {
		mounted += 1;
		appendScripts();
		return () => {
			mounted -= 1;
			syncSessionSheets();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="BusinessSession--container">
			{isLoading ? (
				<div className="BusinessSession--loader">
					<Loader />
				</div>
			) : (
				<div
					className="BusinessSession--container--web-component"
					dangerouslySetInnerHTML={{
						__html: `<session-details
							session_id=${sessionId}
							token=${userDetails.accessToken}
							comments="true"
							env=${envHost}
							type="sessions"
							compliance_data="false"
							url="/super-admin-session-details"
							action_disable="true"
							session_type="complex"
							is_pdf="false"
							session_id_list=${JSON.stringify(signUpSessionStatusState?.sessionIds)}
							is_admin="true"
							businessId=${signUpSessionStatusState?.businessId}
							custom_loader="true"
						/>`,
					}}
				/>
			)}
		</div>
	);
};
